import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X } from 'lucide-react';

const QASection: React.FC = () => {
  const [openQuestions, setOpenQuestions] = useState<number[]>([]);

  const faqs = [
    {
      question: "Is my company a good fit for Energent.ai?",
      answer: "Yes - If you're an energy company looking to modernize operations and boost efficiency, Energent.ai is for you."
    },
    {
      question: "How long does it take to implement my requests?",
      answer: "Implementation timelines vary based on complexity, but most requests are completed within 2-5 business days. Our team works efficiently to ensure minimal disruption to your operations."
    },
    {
      question: "Are your solutions secure?",
      answer: "Absolutely. We implement enterprise-grade security measures including end-to-end encryption, SOC 2 compliance, and regular security audits. Your data is protected with bank-level security protocols."
    },
    {
      question: "Can I get more developers than 3?",
      answer: "Yes, our Enterprise plan supports unlimited team members and concurrent executions. We can scale our development resources to match your project requirements and timelines."
    },
    {
      question: "Do you offer continuous support?",
      answer: "Yes, we provide 24/7 monitoring and support across all our plans. Our Executive and Enterprise plans include dedicated support with SLA guarantees to ensure your systems run smoothly."
    },
    {
      question: "Can I cancel my subscription at any time?",
      answer: "Yes, you can cancel your subscription at any time. We offer flexible monthly and annual billing options with no long-term commitments required."
    }
  ];

  const toggleQuestion = (index: number) => {
    setOpenQuestions(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  return (
    <section className="py-24 bg-black relative overflow-hidden">
      <div className="container mx-auto px-6">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-4">
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Answers</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            We've gone ahead and answered some of the questions you might have.
          </p>
        </motion.div>

        {/* FAQ Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden hover:border-gray-700 transition-all duration-300"
            >
              {/* Question Header */}
              <button
                onClick={() => toggleQuestion(index)}
                className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-800/30 transition-colors duration-200"
              >
                <h3 className="text-white font-medium text-lg pr-4">
                  {faq.question}
                </h3>
                <motion.div
                  animate={{ rotate: openQuestions.includes(index) ? 45 : 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex-shrink-0"
                >
                  {openQuestions.includes(index) ? (
                    <X className="w-5 h-5 text-gray-400" />
                  ) : (
                    <Plus className="w-5 h-5 text-gray-400" />
                  )}
                </motion.div>
              </button>

              {/* Answer Content */}
              <AnimatePresence>
                {openQuestions.includes(index) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6">
                      <div className="border-t border-gray-700/50 pt-4">
                        <p className="text-gray-300 text-sm leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Floating Animation Elements */}
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-blue-400/40 rounded-full animate-pulse"></div>
        <div className="absolute bottom-1/3 right-1/3 w-1 h-1 bg-cyan-400/30 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 right-1/4 w-1.5 h-1.5 bg-blue-300/50 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>
    </section>
  );
};

export default QASection;